"use client";

import { useState } from "react";
import Link from "next/link";
import { Check, X, Shield, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PLANS } from "@/lib/stripe";
import type { BillingInterval } from "@/types";

const COMPARISON_ROWS: { label: string; category: "qa" | "security"; free: string | boolean; pro: string | boolean }[] = [
  { label: "QA test runs / month", category: "qa", free: "10", pro: "500" },
  { label: "AI browser agent", category: "qa", free: false, pro: true },
  { label: "Visual regression testing", category: "qa", free: false, pro: true },
  { label: "GitHub PR integration", category: "qa", free: false, pro: true },
  { label: "Slack / Discord alerts", category: "qa", free: false, pro: true },
  { label: "Security scans / month", category: "security", free: "1", pro: "Unlimited" },
  { label: "Exposed secrets detection", category: "security", free: true, pro: true },
  { label: "Security headers check", category: "security", free: true, pro: true },
  { label: "SSL/TLS validation", category: "security", free: true, pro: true },
  { label: "CORS misconfiguration", category: "security", free: false, pro: true },
  { label: "Open redirect detection", category: "security", free: false, pro: true },
  { label: "Broken auth / lockout check", category: "security", free: false, pro: true },
  { label: "Rate limiting probe", category: "security", free: false, pro: true },
  { label: "Path exposure scan", category: "security", free: false, pro: true },
  { label: "Error leakage detection", category: "security", free: false, pro: true },
  { label: "Combined QA + security report", category: "security", free: false, pro: true },
];

function CellValue({ value, isPopular }: { value: string | boolean; isPopular: boolean }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className={cn("h-4 w-4 mx-auto", isPopular ? "text-background" : "text-foreground")} />
    ) : (
      <X className="h-4 w-4 mx-auto text-muted-foreground/40" />
    );
  }
  return <span className={cn("text-sm font-medium", isPopular ? "text-background" : "")}>{value}</span>;
}

export default function PricingPage() {
  const [interval, setInterval] = useState<BillingInterval>("monthly");

  return (
    <div className="container mx-auto max-w-5xl px-4 lg:px-8 py-16 md:py-24">
      {/* Header */}
      <div className="mb-14 text-center">
        <span className="text-sm text-muted-foreground uppercase tracking-wider">Pricing</span>
        <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight">
          QA + Security. One price.
        </h1>
        <p className="mt-4 text-muted-foreground max-w-md mx-auto">
          Stop paying for separate QA and security tools. Axlynq does both — free to start.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="mb-10 flex items-center justify-center gap-4">
        <div className="inline-flex items-center rounded-full border border-border p-1">
          {(["monthly", "yearly"] as BillingInterval[]).map((i) => (
            <button
              key={i}
              onClick={() => setInterval(i)}
              className={cn(
                "rounded-full px-5 py-2 text-sm font-medium transition-colors",
                interval === i ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {i === "monthly" ? "Monthly" : "Yearly"}
            </button>
          ))}
        </div>
        {interval === "yearly" && (
          <span className="text-sm font-medium text-foreground bg-muted px-2.5 py-1 rounded-full">Save 20%</span>
        )}
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {Object.values(PLANS).map((plan) => {
          const price = interval === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
          const isPopular = plan.tier === "PRO";

          return (
            <div
              key={plan.name}
              className={cn(
                "relative flex flex-col rounded-2xl border p-8",
                isPopular ? "border-foreground bg-foreground text-background" : "border-border bg-card"
              )}
            >
              {isPopular && (
                <div className="absolute -top-3 left-8 rounded-full bg-background text-foreground border border-border px-3 py-1 text-xs font-semibold">
                  Most Popular
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                <p className={cn("mt-1 text-sm", isPopular ? "text-background/70" : "text-muted-foreground")}>
                  {plan.description}
                </p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">${price}</span>
                  <span className={cn("text-sm", isPopular ? "text-background/60" : "text-muted-foreground")}>
                    /{interval === "monthly" ? "mo" : "yr"}
                  </span>
                </div>
                {plan.tier === "PRO" && interval === "yearly" && (
                  <p className="text-xs mt-1 text-background/60">Billed ${plan.yearlyPrice}/year</p>
                )}
              </div>

              <Button
                className={cn(
                  "mb-6 w-full rounded-full",
                  isPopular ? "bg-background text-foreground hover:bg-background/90" : "bg-foreground text-background hover:bg-foreground/90"
                )}
                asChild
              >
                <Link href={plan.tier === "FREE" ? "/register" : `/register?plan=${plan.tier.toLowerCase()}`}>
                  {plan.tier === "FREE" ? "Get started free" : "Start Pro — 14-day trial"}
                </Link>
              </Button>

              <ul className="flex-1 space-y-2.5">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <div className={cn("mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full", isPopular ? "bg-background/20" : "bg-foreground/8")}>
                      <Check className={cn("h-2.5 w-2.5", isPopular ? "text-background" : "text-foreground")} />
                    </div>
                    <span className={isPopular ? "text-background/90" : "text-foreground/80"}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8 text-center">Full feature comparison</h2>
        <div className="rounded-2xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-3 bg-muted/50 border-b border-border px-6 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <div>Feature</div>
            <div className="text-center">Free</div>
            <div className="text-center">Pro</div>
          </div>

          {/* QA section */}
          <div className="px-6 py-2.5 bg-muted/20 border-b border-border flex items-center gap-2">
            <Bug className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">QA Testing</span>
          </div>
          {COMPARISON_ROWS.filter((r) => r.category === "qa").map((row, i, arr) => (
            <div
              key={row.label}
              className={cn("grid grid-cols-3 px-6 py-3.5 text-sm items-center", i < arr.length - 1 ? "border-b border-border/50" : "")}
            >
              <span className="text-foreground/80">{row.label}</span>
              <div className="text-center"><CellValue value={row.free} isPopular={false} /></div>
              <div className="text-center"><CellValue value={row.pro} isPopular={false} /></div>
            </div>
          ))}

          {/* Security section */}
          <div className="px-6 py-2.5 bg-muted/20 border-y border-border flex items-center gap-2">
            <Shield className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Security Scanning</span>
          </div>
          {COMPARISON_ROWS.filter((r) => r.category === "security").map((row, i, arr) => (
            <div
              key={row.label}
              className={cn("grid grid-cols-3 px-6 py-3.5 text-sm items-center", i < arr.length - 1 ? "border-b border-border/50" : "")}
            >
              <span className="text-foreground/80">{row.label}</span>
              <div className="text-center"><CellValue value={row.free} isPopular={false} /></div>
              <div className="text-center"><CellValue value={row.pro} isPopular={false} /></div>
            </div>
          ))}
        </div>
      </div>

      {/* Enterprise CTA */}
      <div className="mb-16 rounded-2xl border border-border bg-card p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-semibold mb-1">Enterprise</h3>
            <p className="text-muted-foreground text-sm max-w-md">
              Unlimited scans, custom integrations, SSO, dedicated support, and SLA. Contact us for a custom quote.
            </p>
          </div>
          <Button variant="outline" className="rounded-full px-6 shrink-0" asChild>
            <Link href="mailto:support@axlynq.com">Contact sales</Link>
          </Button>
        </div>
      </div>

      {/* FAQ */}
      <div className="grid md:grid-cols-2 gap-8">
        {[
          { q: "Does Free include security scanning?", a: "Yes — 1 security scan per month covering secrets, headers, and SSL. Pro unlocks the full suite: CORS, auth, rate limiting, path exposure, and more." },
          { q: "Can I change plans anytime?", a: "Yes. Upgrade or downgrade at any time. Changes take effect immediately with prorated billing." },
          { q: "Is there a free trial for Pro?", a: "Yes — 14-day free trial on Pro, no credit card required." },
          { q: "Do you offer refunds?", a: "Yes, 30-day money-back guarantee on all paid plans. No questions asked." },
        ].map(({ q, a }) => (
          <div key={q}>
            <h4 className="font-semibold mb-2">{q}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
